<template>
  <v-dialog
    v-model="dialog"
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 700 : 'auto'"
    max-height="80vh"
  >
    <v-card variant="flat" data-cy="dialog-announcements-modal">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="d-flex align-center" data-cy="dialog-announcements-title">
          <v-icon class="mr-2">mdi-bullhorn</v-icon>
          {{ $t('announcements.dialog.title') }}
        </span>
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')" data-cy="dialog-announcements-close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <div v-if="isLoading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" :aria-label="$t('common.loading')" />
        </div>

        <div v-else-if="visibleAnnouncements.length === 0" class="text-center py-8 text-medium-emphasis">
          {{ $t('announcements.dialog.noAnnouncements') }}
        </div>

        <div v-else class="announcements-list">
          <v-card
            v-for="announcement in visibleAnnouncements"
            :key="announcement.id"
            class="mb-4 announcement-card"
            variant="outlined"
          >
            <v-card-title class="text-h6 announcement-title d-flex justify-space-between align-center">
              <span>{{ announcement.title }}</span>
              <v-btn
                v-if="!announcement.persistent"
                icon
                variant="text"
                size="small"
                @click="handleDismissAnnouncement(announcement.id)"
                :aria-label="$t('announcements.dialog.dismiss')"
                :loading="dismissingIds.includes(announcement.id)"
              >
                <v-icon size="small">mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="announcement-body">
              <MdPreview
                :modelValue="announcement.body"
                :theme="mdTheme"
                language="en-US"
                class="announcement-content"
              />
            </v-card-text>
          </v-card>
        </div>

        <div v-if="errorMessage" class="inline-message--error mt-2">
          {{ errorMessage }}
        </div>
      </v-card-text>

      <v-card-actions class="d-flex justify-space-between align-center">
        <v-checkbox
          v-model="dontShowAgain"
          :label="$t('announcements.dialog.dontShowAgain')"
          hide-details
          density="compact"
          class="dont-show-checkbox"
        />
        <v-btn size="small" variant="text" @click="dialog = false">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { MdPreview } from 'md-editor-v3';
import { useAnnouncementStore } from '@/stores/AnnouncementStore.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  name: 'AnnouncementsDialog',
  mixins: [DialogModel],
  components: {
    MdPreview,
  },
  data() {
    return {
      errorMessage: '',
      dontShowAgain: false,
      dismissingIds: [],
    };
  },
  computed: {
    announcementStore() {
      return useAnnouncementStore();
    },
    isLoading() {
      return this.announcementStore.isLoading;
    },
    visibleAnnouncements() {
      return this.announcementStore.getVisibleAnnouncements;
    },
    mdTheme() {
      return this.$vuetify.theme.current.dark ? 'dark' : 'light';
    },
  },
  watch: {
    dialog(newValue, oldValue) {
      if (newValue) {
        // Reset state when dialog opens
        this.errorMessage = '';
        this.dontShowAgain = this.announcementStore.isDialogDismissed();
        this.dismissingIds = [];
      }
      // When dialog is closed, handle the "Don't show again" checkbox state
      if (oldValue === true && newValue === false) {
        if (this.dontShowAgain) {
          // Set localStorage flag to prevent auto-show on login
          this.announcementStore.setDialogDismissed();
        } else {
          // Clear localStorage flag to re-enable auto-show
          this.announcementStore.clearDialogDismissedState();
        }
      }
    },
  },
  methods: {
    async handleDismissAnnouncement(id) {
      this.dismissingIds.push(id);
      this.errorMessage = '';
      try {
        await this.announcementStore.dismissAnnouncement(id);
      } catch (err) {
        this.errorMessage = this.$t('announcements.dialog.dismissError');
        console.error('Failed to dismiss announcement:', err);
      } finally {
        this.dismissingIds = this.dismissingIds.filter(dismissId => dismissId !== id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.announcements-list {
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px;
}

.announcement-card {
  border-color: rgb(var(--v-theme-primary));
}

.announcement-title {
  font-weight: 600;
  padding-bottom: 8px;
  min-height: 36px;
}

.announcement-body {
  padding: 8px;
}

.announcement-content {
  :deep(.md-editor-preview-wrapper) {
    padding: 0;
    background: transparent;
  }

  :deep(.md-editor-preview) {
    background: transparent;
    padding: 0.75rem;
    font-size: 0.95rem;
    line-height: 1.6;

    p {
      margin-bottom: 0.75rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul, ol {
      margin-bottom: 0.75rem;
      padding-left: 1.5rem;
    }

    a {
      color: rgb(var(--v-theme-primary));
      text-decoration: underline;
    }
  }

  border: none;
}

.dont-show-checkbox {
  flex: 0 1 auto;
}
</style>
